import { Fragment, createElement as rc, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { metadata } from 'lib_ui-services';
import useDbView from '../../hooks/useDbView';
import useIsInActiveRoute from '../../hooks/useIsInActiveRoute';
const { getPrettyRelationName } = metadata;

import {
    DisabledMenuButton,
    LinkText,
    MenuButton,
    NavIcon,
    NavigationButton,
    NoIcon,
    disabledNavigationButton
} from './styles.gl2025';
import { useTheme } from 'styled-components';
import { NavbarContext } from '../appLayout/NavBarContextProvider.gl2025';

const _p = {
    getPrettyRelationName,
    useDbView
};

export const _private = _p;

const ELLIPSES = '…';
const MAX_TITLE_LENGTH = 20;

function NavHeadingRecordList(props) {
    const {
        id: _id,
        currentRoute,
        hNode,
        hNode: { namespace, relation, iconName }
    } = props || { hNode: {} };
    const id = _id || hNode.id;

    // we need to communicate the selected record to the page.
    // We have different options for this:
    // react-router URL parameters (e.g. /Inventory/Take/:id)
    // But the Page title is taken from the last part of the path so
    // e.g. /Inventory/Take/1234 would display "1234" as page title
    // It also would require updating the route handling
    // Another option is to add it into the query like:
    // /Inventory/Take?inventory:inventory=1234
    // That would still show "Take" as Page title,
    // as the the query is already excluded from the path before taking the title off of it.
    // Which means: less changes/less risks of bugs if we go that route
    // So, stick the id in the query/search portion:
    const subRoute = useMemo(() => {
        return `${currentRoute}?${namespace}_${relation}=`;
    }, [namespace, relation, currentRoute]);
    const theme = useTheme();

    const { records, recordCount, viewReady } = _p.useDbView(
        namespace,
        relation,
        `${namespace}_${relation}_menu_${hNode.id}`,
        hNode
    );

    const disabledMessage = useMemo(() => {
        if (!viewReady) return 'Loading';
        const prettyRelationName = _p.getPrettyRelationName(namespace, relation);
        return `No ${prettyRelationName || 'Records'} Available`;
    }, [namespace, relation, viewReady]);

    if (!recordCount || !viewReady) {
        // prettier-ignore
        return rc(disabledNavigationButton,null,
            rc(DisabledMenuButton, { id, title: disabledMessage  }, // icon (if any)
                iconName && rc(NavIcon, { fontColor: theme.outerMenus.disabledFontColor, name:iconName }, iconName),
                !iconName && rc(NoIcon),
                rc(LinkText, { disabled: true }, disabledMessage)
            )
        );
    }

    // prettier-ignore
    return rc(Fragment, null,
        records.map(record => rc(ListEntry,{key: record._id, subRoute, record}))
    );
}

function ListEntry({ subRoute, record }) {
    const currentRoute = subRoute + record._id;
    const isActive = useIsInActiveRoute({ currentRoute });
    const theme = useTheme();
    const { open: navOpen, setOpen, locked: navLocked, setLocked } = useContext(NavbarContext);

    const title = useMemo(() => {
        return record.title.length > MAX_TITLE_LENGTH
            ? record.title.substring(0, MAX_TITLE_LENGTH - 2) + ELLIPSES
            : record.title;
    }, [record.title]);

    const closeSidebarOnMobile = useCallback(() => {
        if (theme.mobile) {
            setOpen(false);
            setLocked(false);
        }
    }, [setOpen, setLocked, theme.mobile]);

    const iconOnly = !navLocked && !navOpen;

    // prettier-ignore
    return rc(NavigationButton, { to: currentRoute, onClick: closeSidebarOnMobile },
        rc(MenuButton, { id: record._id, selected: isActive, title: record.title, iconOnly }, 
            rc(NoIcon), 
            rc(LinkText, { hidden: iconOnly, selected: isActive }, title)
        )
    );
}

NavHeadingRecordList.propTypes = {
    id: PropTypes.string,
    currentRoute: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
    hNode: PropTypes.object
};

export default NavHeadingRecordList;
